export type VideoInfo =
  | { type: 'youtube'; videoId: string; embedUrl: string; thumbnailUrl: string; watchUrl: string; aspectRatio: number }
  | { type: 'vimeo'; videoId: string; embedUrl: string; thumbnailUrl?: string; watchUrl: string; aspectRatio: number }
  | { type: 'direct'; url: string; mimeHint: string; aspectRatio?: number }
  | null;

const DIRECT_VIDEO_EXTENSIONS = ['.mp4', '.webm', '.ogg', '.mov', '.m4v', '.mkv'];

export function parseVideoUrl(url: string): VideoInfo {
  if (!url) return null;

  const trimmed = url.trim();
  const lower = trimmed.toLowerCase();

  // YouTube
  const shortsMatch = trimmed.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/);
  if (shortsMatch) {
    const videoId = shortsMatch[1];
    return {
      type: 'youtube',
      videoId,
      embedUrl: `https://www.youtube.com/embed/${videoId}?rel=0`,
      thumbnailUrl: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
      watchUrl: `https://www.youtube.com/watch?v=${videoId}`,
      aspectRatio: 9 / 16,
    };
  }

  const ytMatch = trimmed.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/);
  if (ytMatch) {
    const videoId = ytMatch[1];
    return {
      type: 'youtube',
      videoId,
      embedUrl: `https://www.youtube.com/embed/${videoId}?rel=0`,
      thumbnailUrl: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
      watchUrl: `https://www.youtube.com/watch?v=${videoId}`,
      aspectRatio: 16 / 9,
    };
  }

  // Vimeo
  const vimeoMatch = trimmed.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeoMatch) {
    const videoId = vimeoMatch[1];
    return {
      type: 'vimeo',
      videoId,
      embedUrl: `https://player.vimeo.com/video/${videoId}`,
      watchUrl: `https://vimeo.com/${videoId}`,
      aspectRatio: 16 / 9,
    };
  }

  // Direct video file
  if (DIRECT_VIDEO_EXTENSIONS.some((ext) => lower.endsWith(ext))) {
    return {
      type: 'direct',
      url: trimmed,
      mimeHint: lower.endsWith('.webm') ? 'video/webm' : lower.endsWith('.ogg') ? 'video/ogg' : 'video/mp4',
    };
  }

  return null;
}

export function isVideoUrl(url: string): boolean {
  return !!parseVideoUrl(url);
}
